import { ShieldAlert } from "lucide-react";
import { Link, NavLink, useParams } from "react-router-dom";

import { AuditTab } from "../components/admin/audit";
import { HealthTab } from "../components/admin/health";
import { ModulesTab } from "../components/admin/modules";
import { SpendTab } from "../components/admin/spend";
import { ThresholdTab } from "../components/admin/threshold";
import { TracesTab } from "../components/admin/traces";
import { RequireRole } from "../components/RequireRole";
import { Loading } from "../components/States";
import { roleBlock, useMe, type Me } from "../lib/me";
import { useNeioStatus } from "../lib/neio";

export const ADMIN_TABS = [
  { key: "audit", label: "Audit log" },
  { key: "spend", label: "Spend" },
  { key: "settings", label: "Settings" },
  { key: "modules", label: "Module switches" },
  { key: "health", label: "Health" },
] as const;

/** Shown only where `GET /neio/status` says NeIO Core is on. */
export const NEIO_TAB = { key: "traces", label: "NeIO traces" } as const;

type TabKey = (typeof ADMIN_TABS)[number]["key"] | typeof NEIO_TAB.key;

function TabBody({ tab, me }: { tab: TabKey; me: Me }) {
  switch (tab) {
    case "spend": return <SpendTab />;
    case "settings": return <ThresholdTab />;
    case "modules": return <ModulesTab />;
    case "health": return <HealthTab />;
    case "traces": return <TracesTab />;
    default: return <AuditTab packages={me.packages ?? null} />;
  }
}

/** What anyone but an admin sees at an /admin URL: why, and a way back. */
export function NotForYou({ me }: { me: Me | null }) {
  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 12 }}>Admin</h2>
      <div className="state-box" role="status">
        <ShieldAlert aria-hidden="true" />
        <div>
          <div className="state-title">This screen is for administrators</div>
          <p className="muted">{roleBlock(me, "admin")}</p>
          <p className="muted">
            Roles are set in the deployment settings. Ask the person who runs this environment if you need admin access.
          </p>
          <p><Link to="/">Back to the dashboard</Link></p>
        </div>
      </div>
    </div>
  );
}

/** /admin/:tab. Admins only; the API refuses everyone else too. */
export function Admin() {
  const { me } = useMe();
  const { tab = "audit" } = useParams();
  const { enabled: neio } = useNeioStatus();
  const tabs: { key: TabKey; label: string }[] = neio ? [...ADMIN_TABS, NEIO_TAB] : [...ADMIN_TABS];
  const current = tabs.find((t) => t.key === tab) ?? ADMIN_TABS[0];
  if (me === undefined) return <div className="page"><Loading what="Checking your role" /></div>;
  return (
    <RequireRole role="admin" me={me} fallback={<NotForYou me={me} />}>
      <div className="page">
        <h2 style={{ fontSize: 26, marginBottom: 12 }}>Admin</h2>
        <nav className="tabs" aria-label="Admin sections">
          {tabs.map((t) => (
            <NavLink key={t.key} to={`/admin/${t.key}`} className={() => (t.key === current.key ? "active" : "")}
              aria-current={t.key === current.key ? "page" : undefined}>
              {t.label}
            </NavLink>
          ))}
        </nav>
        <h3 style={{ fontSize: 18, marginBottom: 8 }}>{current.label}</h3>
        {me && <TabBody tab={current.key} me={me} />}
      </div>
    </RequireRole>
  );
}
